// =============================================================================
// MobileAnalytics.tsx
// Mobile Analytics tab — reads pre-aggregated data from the orders/analytics
// edge action and renders a mobile-tuned set of charts.
//
// Motion (Stage 3): every chart starts at "empty" geometry on first paint and
// animates to its target on mount. View-switching (Progress ↔ Budget) flips
// `animKey` which is used as a React `key` on the wrapper, forcing unmount/
// remount and replaying the entry animations. Mirrors the desktop
// AnalyticsTab pattern so the two views feel like one design system.
// =============================================================================

import React, { useEffect, useMemo, useState } from 'react';

import { Dropdown } from '../ui';

import { fetchAnalytics } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';

import { AnalyticsData, AnalyticsFilters } from '../../lib/types';

import { S, CHART_COLORS } from './mobileStyles';
import { DateRange, labelForRange } from './sheets/DateRangePicker';
import { MiniDonut }    from './charts/MiniDonut';
import { RingProgress } from './charts/RingProgress';
import { TrendBar }     from './charts/TrendBar';
import { TeamRow }      from './charts/TeamRow';
import { LegendRow }    from './charts/LegendRow';

// ── TYPES ────────────────────────────────────────────────────────────

interface MobileAnalyticsProps {
  currency: string;
  analyticsView: 'activity' | 'spend';
  setAnalyticsView: (v: 'activity' | 'spend') => void;
  filterCat: string;
  setFilterCat: (v: string) => void;
  categories: string[];
  dateRange: DateRange;
  onOpenDateRange: () => void;
}

// ── MAIN COMPONENT ───────────────────────────────────────────────────

export default function MobileAnalytics({
  currency, analyticsView, setAnalyticsView,
  filterCat, setFilterCat, categories,
  dateRange, onOpenDateRange,
}: MobileAnalyticsProps) {

  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  // Bumped on view switch (Progress ↔ Budget) and on category change so the
  // wrapper's React `key` flips, unmounting and remounting the chart subtree
  // — which replays every entry animation.
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    const filters: AnalyticsFilters = {
      category: filterCat !== 'all' ? filterCat : undefined,
      date_from: dateRange.from || undefined,
      date_to:   dateRange.to   || undefined,
    };
    fetchAnalytics(filters).then(res => {
      if (res.ok && res.data) setAnalyticsData(res.data);
      setLoading(false);
    });
  }, [filterCat, dateRange.from, dateRange.to]);

  const handleViewChange = (v: 'activity' | 'spend') => {
    setAnalyticsView(v);
    setAnimKey(k => k + 1);
  };

  const handleCatChange = (c: string) => {
    setFilterCat(c);
    setAnimKey(k => k + 1);
  };

  // Replay the chart animations when the date range changes — same pattern as
  // category/view changes so the user sees the new dataset render in.
  useEffect(() => { setAnimKey(k => k + 1); }, [dateRange.from, dateRange.to]);

  const dateActive = dateRange.value !== 'all' && (dateRange.from !== '' || dateRange.to !== '');

  // ── derived ──
  const sc            = analyticsData?.status_counts || {};
  const totalOrders   = Object.values(sc).reduce((s, n) => s + n, 0);
  const completedCount = (sc['delivered'] || 0) + (sc['closed'] || 0);
  const completionRate = totalOrders > 0 ? Math.round((completedCount / totalOrders) * 100) : 0;

  const teamActivity      = analyticsData?.team_activity || [];
  const vendorData        = analyticsData?.vendor_breakdown || [];
  const deptData          = analyticsData?.department_totals || [];
  const categorySpendData = analyticsData?.category_spend || [];
  const totalSpend        = categorySpendData.reduce((s, c) => s + c.total, 0);

  const monthlyTrend = analyticsData?.monthly_trend || [];
  const maxMonthSpend = Math.max(...monthlyTrend.map(m => m.spend), 1);

  const vendorDonutData = useMemo(
    () => vendorData.slice(0, 5).map((v, i) => ({ label: v.vendor, value: v.count, color: CHART_COLORS[i % CHART_COLORS.length] })),
    [vendorData],
  );
  const deptDonutData = useMemo(
    () => deptData.map((d, i) => ({ label: d.department_name, value: d.count, color: CHART_COLORS[i % CHART_COLORS.length] })),
    [deptData],
  );

  const statusPipeline: Record<string, number> = {
    Pending:   sc['pending']   || 0,
    Ordered:   sc['ordered']   || 0,
    Delivered: sc['delivered'] || 0,
    Closed:    sc['closed']    || 0,
  };

  const PIPELINE_COLOURS: Record<string, { bg: string; text: string }> = {
    Pending:   { bg: 'var(--red-bg)',   text: 'var(--red)'   },
    Ordered:   { bg: 'var(--blue-bg)',  text: 'var(--blue)'  },
    Delivered: { bg: 'var(--teal-bg)',  text: 'var(--teal)'  },
    Closed:    { bg: 'var(--surface2)', text: 'var(--text2)' },
  };

  return (
    <div className="m-tab-content">
      {/* Two-row control layout — pinned so the category dropdown never
          reflows when the date chip's label changes width:
            Row 1: Progress/Budget segmented (left)  ·  date chip (right)
            Row 2: All categories dropdown (full width)
          Outer column flex with gap; row 1 is a flex with a spacer so the
          chip can grow horizontally without bumping anything off-row. */}
      <div style={{ ...S.card, padding: '8px 10px', marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)', flexShrink: 0 }}>
            <button onClick={() => handleViewChange('activity')} style={{ padding: '6px 16px', fontSize: 'var(--m-text-button)', fontWeight: 600, borderRadius: 6, border: 'none', background: analyticsView === 'activity' ? 'var(--accent)' : 'transparent', color: analyticsView === 'activity' ? 'white' : 'var(--text3)', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s ease' }}>Progress</button>
            <button onClick={() => handleViewChange('spend')}    style={{ padding: '6px 16px', fontSize: 'var(--m-text-button)', fontWeight: 600, borderRadius: 6, border: 'none', background: analyticsView === 'spend'    ? 'var(--accent)' : 'transparent', color: analyticsView === 'spend'    ? 'white' : 'var(--text3)', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s ease' }}>Budget</button>
          </div>
          <div style={{ flex: 1 }} />
          <button
            onClick={onOpenDateRange}
            className={`filter-chip ${dateActive ? 'is-active' : ''}`}
            style={{ flexShrink: 0, maxWidth: '60%', overflow: 'hidden' }}
            title={labelForRange(dateRange)}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{labelForRange(dateRange)}</span>
            <svg viewBox="0 0 12 12" width="10" height="10" fill="currentColor" style={{ opacity: .6, flexShrink: 0 }}>
              <path d="M2 4l4 4 4-4" />
            </svg>
          </button>
        </div>
        <Dropdown
          value={filterCat}
          options={[
            { value: 'all', label: 'All categories' },
            ...categories.map(c => ({ value: c, label: c })),
          ]}
          onChange={handleCatChange}
          width="100%"
        />
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 'var(--m-text-label)', color: 'var(--text3)' }}>Loading…</div>
      )}

      {!loading && (
        <div key={`view-${analyticsView}-${animKey}`}>

          {/* Headline stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
            <div style={{ background: 'var(--surface)', padding: 10, borderRadius: 10 }}>
              <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text2)', marginBottom: 4 }}>{analyticsView === 'spend' ? 'Total spend' : 'Total orders'}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent)' }}>{analyticsView === 'spend' ? formatCurrency(totalSpend, currency, 'rounded') : totalOrders}</div>
            </div>
            <div style={{ background: 'var(--surface)', padding: 10, borderRadius: 10 }}>
              <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text2)', marginBottom: 4 }}>{analyticsView === 'spend' ? 'Orders' : 'Completion'}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{analyticsView === 'spend' ? totalOrders : `${completionRate}%`}</div>
            </div>
          </div>

          {analyticsView === 'activity' && (
            <>
              {/* Ring + pipeline */}
              <div style={{ ...S.card, padding: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <RingProgress pct={completionRate} label="Completed" color="var(--accent)" size={72} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, marginBottom: 6 }}>Pipeline</div>
                    <div style={{ display: 'flex', gap: 2, height: 20, borderRadius: 4, overflow: 'hidden' }}>
                      {(['Pending', 'Ordered', 'Delivered', 'Closed'] as const).map(s => {
                        const c = statusPipeline[s] || 0;
                        if (c === 0) return null;
                        const colours = PIPELINE_COLOURS[s];
                        return <div key={s} style={{ flex: c, background: colours.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 500, color: colours.text, minWidth: 16 }}>{c}</div>;
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {teamActivity.length > 0 && (
                <div style={{ ...S.card, padding: 12, marginBottom: 8 }}>
                  <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, marginBottom: 8 }}>Team activity</div>
                  {teamActivity.slice(0, 5).map((t, i) => {
                    const max = teamActivity[0]?.count || 1;
                    return (
                      <TeamRow key={t.created_by} name={t.created_by_name || 'Unknown'} count={t.count} max={max} index={i} />
                    );
                  })}
                </div>
              )}
            </>
          )}

          {analyticsView === 'spend' && (
            <>
              <div style={{ ...S.card, padding: 12, marginBottom: 8 }}>
                <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, marginBottom: 10 }}>Spend trend</div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 60 }}>
                  {monthlyTrend.map((m, i) => (
                    <TrendBar
                      key={m.month}
                      spend={m.spend}
                      maxSpend={maxMonthSpend}
                      isDominant={m.spend > 0 && m.spend === maxMonthSpend}
                      label={m.label.split(' ')[0]}
                      valueLabel={formatCurrency(m.spend, currency, 'rounded')}
                      delay={i * 80}
                    />
                  ))}
                </div>
              </div>

              {categorySpendData.length > 0 && (
                <div style={{ ...S.card, padding: 12, marginBottom: 8 }}>
                  <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, marginBottom: 8 }}>By category</div>
                  {categorySpendData.map((c, i) => (
                    <LegendRow
                      key={c.category}
                      color={CHART_COLORS[i % CHART_COLORS.length]}
                      name={c.category}
                      index={i}
                      value={
                        <>
                          <span style={{ color: 'var(--text3)', marginRight: 6 }}>{c.count} orders</span>
                          <span style={{ fontWeight: 600 }}>{formatCurrency(c.total, currency, 'rounded')}</span>
                        </>
                      }
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Vendor breakdown */}
          <div style={{ ...S.card, padding: 12, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {vendorDonutData.length > 0 && <MiniDonut data={vendorDonutData} size={64} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, marginBottom: 6 }}>By vendor</div>
                {vendorData.slice(0, 5).map((v, i) => (
                  <LegendRow
                    key={v.vendor}
                    color={CHART_COLORS[i % CHART_COLORS.length]}
                    name={v.vendor}
                    index={i}
                    value={<span style={{ color: 'var(--text3)' }}>{v.count}</span>}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Department breakdown */}
          <div style={{ ...S.card, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {deptDonutData.length > 0 && <MiniDonut data={deptDonutData} size={64} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, marginBottom: 6 }}>By department</div>
                {deptData.map((d, i) => (
                  <LegendRow
                    key={d.department_id}
                    color={CHART_COLORS[i % CHART_COLORS.length]}
                    name={d.department_name}
                    index={i}
                    value={<span style={{ color: 'var(--text3)' }}>{d.count}</span>}
                  />
                ))}
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
